import 'data-layer-helper/dist/data-layer-helper';
import Cookies from 'js-cookie';
import { buildEventParams } from './eventParams.js';

const PAGE_VIEW_READY_COOKIE_NAME = '_ga_page_view_ready';
const PAGE_VIEW_EVENT_NAME = 'page_view';

/**
 * Liest GA4-Events aus `window.ga4DataLayer` und leitet sie via `gtag('event', …)`
 * weiter. Stellt zwei Gates sicher:
 *
 * 1. Consent-Entscheidung: Events werden gequeuet bis ein `gtag('consent', 'update', …)`
 *    Signal in `window.dataLayer` ankommt — egal ob granted oder denied.
 *    Server-GTM entscheidet anhand des Consent-States, ob das Event tatsächlich an
 *    GA4 weiterverarbeitet wird; der Browser muss nur warten bis die Entscheidung
 *    gefallen ist, damit gtag die Events mit korrektem Consent-Payload versieht.
 *
 * 2. page_view-Reihenfolge: page_view muss das erste Event sein, das gtag aufruft.
 *    Server-GTM verlässt sich zusätzlich auf das `_ga_page_view_ready`-Cookie.
 *
 * gtag selbst pusht in `window.gtagDataLayer` (siehe gtag.hbs `?l=gtagDataLayer`),
 * isoliert vom GTM- und Listener-DataLayer.
 *
 * In der App-Webview übernimmt stattdessen der `FirebaseWebviewForwarder`; beide
 * Gates sind dort gegenstandslos, weil kein Server-GTM beteiligt ist.
 */
class GA4Forwarder {
    static #initialized = false;

    constructor() {
        this.queue = [];
        this.consentReceived = false;
        this.pageViewSent = false;

        this._deletePageViewReadyCookie();
        this._initConsentListener();
        this._initEventListener();
    }

    static initOnce() {
        if (GA4Forwarder.#initialized) {
            return;
        }
        GA4Forwarder.#initialized = true;
        new GA4Forwarder();
    }

    static _resetForTests() {
        GA4Forwarder.#initialized = false;
    }

    _initConsentListener() {
        if (typeof window === 'undefined' || typeof window.DataLayerHelper === 'undefined') {
            return;
        }
        window.dataLayer = window.dataLayer || [];
        new window.DataLayerHelper(window.dataLayer, this._handleConsentMessage.bind(this), true);
    }

    _handleConsentMessage(model, message) {
        if (this._isConsentUpdate(message)) {
            this._onConsentReceived();
        }
    }

    /**
     * Erkennt `gtag('consent', 'update', …)`-Signale (granted ODER denied).
     * Greift sowohl bei Direct-Array-Pushes (`dataLayer.push(['consent', 'update', …])`)
     * als auch bei Arguments-Pushes vom gtag-Wrapper (`dataLayer.push(arguments)`),
     * weil beide indizierte Zugriffe `[0]`, `[1]` zulassen.
     * @private
     */
    _isConsentUpdate(message) {
        if (!message || typeof message !== 'object') {
            return false;
        }
        return message[0] === 'consent' && message[1] === 'update';
    }

    _initEventListener() {
        if (typeof window === 'undefined' || typeof window.DataLayerHelper === 'undefined') {
            return;
        }
        window.ga4DataLayer = window.ga4DataLayer || [];
        new window.DataLayerHelper(window.ga4DataLayer, this._handleGa4Message.bind(this), true);
    }

    _handleGa4Message(model, message) {
        // Nur Plain-Object-Pushes mit `.event` verarbeiten — gtag-Auto-Pushes
        // landen zwar in `gtagDataLayer`, aber defensiv prüfen wir trotzdem.
        if (!message || typeof message.event !== 'string') {
            return;
        }
        this._enqueue(message);
    }

    _onConsentReceived() {
        if (this.consentReceived) {
            return;
        }
        this.consentReceived = true;
        this._tryFlush();
    }

    _enqueue(eventObject) {
        if (this.consentReceived && this.pageViewSent) {
            this._send(eventObject);
            return;
        }
        this.queue.push(eventObject);
        this._tryFlush();
    }

    _tryFlush() {
        if (!this.consentReceived) {
            return;
        }

        if (this.pageViewSent) {
            for (const event of this.queue) {
                this._send(event);
            }
            this.queue = [];
            return;
        }

        const pageViewIndex = this.queue.findIndex(isPageViewEvent);

        if (pageViewIndex === -1) {
            return;
        }

        const pageView = this.queue[pageViewIndex];
        this._setPageViewReadyCookie(true);
        this._send(pageView);
        this.pageViewSent = true;

        const remaining = this.queue;
        this.queue = [];
        for (const event of remaining) {
            if (event === pageView) {
                continue;
            }
            this._send(event);
        }
    }

    _send(eventObject) {
        if (typeof window === 'undefined' || typeof gtag === 'undefined') {
            console.warn('[GA4Forwarder] gtag is not available, cannot send event', eventObject);
            return;
        }

        const measurementId = window.GA_MEASUREMENT_ID || null;
        if (!measurementId) {
            console.warn('[GA4Forwarder] No measurement ID found, cannot send event', eventObject);
            return;
        }

        const { eventName, params } = buildEventParams(eventObject);

        const eventParams = {
            ...params,
            page_location: window.location.href,
            page_referrer: document.referrer,
            page_title: document.title,
            send_to: measurementId
        };

        gtag('event', eventName, eventParams);
    }

    _setPageViewReadyCookie(value) {
        Cookies.set(PAGE_VIEW_READY_COOKIE_NAME, value);
    }

    _deletePageViewReadyCookie() {
        Cookies.remove(PAGE_VIEW_READY_COOKIE_NAME);
    }
}

function isPageViewEvent(event) {
    return event.event === PAGE_VIEW_EVENT_NAME;
}

export { GA4Forwarder };
